import React, { useState, useEffect, useRef } from 'react';
import './Chat.css';
import { getRankMeta } from '../utils/rankConfig';

// Auto-detect WebSocket URL based on current hostname
const getWebSocketURL = () => {
  // Use environment variable if set
  if (process.env.REACT_APP_CHAT_WS_URL) {
    return process.env.REACT_APP_CHAT_WS_URL;
  }
  
  // In production, use same origin (WebSocket is on same server)
  if (typeof window !== 'undefined') {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const hostname = window.location.hostname;
    const port = window.location.port ? `:${window.location.port}` : '';
    return `${protocol}//${hostname}${port}/ws/chat`;
  }
  
  // Fallback for development
  return 'ws://localhost:5000/ws/chat';
};

const WS_URL = getWebSocketURL();

function Chat({ currentUser, onViewProfile }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const [imageAttachment, setImageAttachment] = useState(null);
  const [attachmentError, setAttachmentError] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [ws, setWs] = useState(null);

  // Private DM state
  const [showDmPanel, setShowDmPanel] = useState(false);
  const [dmTarget, setDmTarget] = useState(null); // userId to DM with
  const [dmMessages, setDmMessages] = useState([]);
  const [usersOnline, setUsersOnline] = useState([]);

  const rankMeta = currentUser ? getRankMeta(currentUser.role) : getRankMeta();
  const permissions = rankMeta.permissions || {};

  useEffect(() => {
    // Open WebSocket on mount
    if (!currentUser) return;
    let socket;
    if (WS_URL) {
      socket = new window.WebSocket(WS_URL);
      setWs(socket);

      socket.onopen = () => {
        setIsConnected(true);
        // Announce presence
        socket.send(
          JSON.stringify({
            type: 'join',
            user: {
              id: currentUser.id,
              username: currentUser.discordUsername,
              role: currentUser.role,
            },
          })
        );
      };

      socket.onclose = () => {
        setIsConnected(false);
      };

      socket.onerror = () => {
        setIsConnected(false);
      };

      socket.onmessage = (event) => {
        let data;
        try {
          data = JSON.parse(event.data);
        } catch (e) {
          return; // Ignore
        }
        // Handle special types
        if (data.type === 'chat') {
          setMessages((msgs) => {
            const merged = [...msgs, data.payload];
            return merged.slice(-200);
          });
        } else if (data.type === 'init') {
          // Full chat dump
          setMessages(data.payload.slice(-200));
        } else if (data.type === 'delete') {
          setMessages((msgs) => msgs.filter((m) => m.id !== data.messageId));
        } else if (data.type === 'users') {
          setUsersOnline(data.users || []);
        } else if (data.type === 'dm') {
          if (
            data.to === currentUser.id ||
            data.from === currentUser.id // so you see your own DMs sent, as confirmation
          ) {
            setDmMessages((prev) => [...prev, data].slice(-100));
          }
        }
        // Ignore unknowns
      };
    }

    return () => {
      if (socket && socket.readyState === 1) {
        socket.close();
      }
    };
    // eslint-disable-next-line
  }, [currentUser]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, dmMessages, showDmPanel]);

  // For getting old messages after connecting if backend supports
  useEffect(() => {
    if (!isConnected || !ws || !currentUser) return;
    ws.send(JSON.stringify({ type: 'init' }));
    // Optionally announce yourself to get the online user list
    ws.send(JSON.stringify({ type: 'getUsers' }));
    // eslint-disable-next-line
  }, [isConnected, ws, currentUser]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!currentUser) {
      alert('Please log in to chat!');
      return;
    }
    if (permissions.canSendWorldMessages === false) {
      alert('Your rank cannot send messages in world chat.');
      return;
    }

    if (!newMessage.trim() && !imageAttachment) return;
    if (imageAttachment && !permissions.canSendWorldImages) {
      setAttachmentError(
        'Only founders or ranks with image permissions can post images here.'
      );
      return;
    }
    if (!ws || ws.readyState !== 1) {
      alert(
        'Chat server is not connected. Wait a moment or refresh the page.'
      );
      return;
    }

    // Build chat message object
    const chatMsg = {
      id: Date.now().toString() + '-' + Math.floor(Math.random() * 10000),
      user: currentUser.discordUsername,
      userId: currentUser.id,
      role: currentUser.role || 'user',
      message: newMessage.trim(),
      image: imageAttachment?.dataUrl || null,
      timestamp: new Date().toISOString(),
      type: 'chat', // for backend routing
    };

    ws.send(JSON.stringify({ type: 'chat', payload: chatMsg }));

    setNewMessage('');
    setImageAttachment(null);
    setAttachmentError('');
    setIsLoading(false);
  };

  // Private DM sender
  const handleSendDm = (e) => {
    e.preventDefault();
    if (!currentUser || !ws) return;
    if (!dmTarget || !dmDmText.trim()) return;
    ws.send(
      JSON.stringify({
        type: 'dm',
        from: currentUser.id,
        username: currentUser.discordUsername,
        to: dmTarget,
        message: dmDmText.trim(),
        timestamp: new Date().toISOString(),
      })
    );
    setDmDmText('');
  };

  const [dmDmText, setDmDmText] = useState('');

  const handleAttachmentChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setAttachmentError('Please select an image file.');
      event.target.value = '';
      return;
    }
    if (file.size > 1024 * 1024) {
      setAttachmentError('Image must be under 1MB for chat.');
      event.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setImageAttachment({
        name: file.name,
        dataUrl: reader.result,
      });
      setAttachmentError('');
    };
    reader.readAsDataURL(file);
  };

  const removeAttachment = () => {
    setImageAttachment(null);
    setAttachmentError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getRoleColor = (role) => getRankMeta(role).color || '#aab1c7';

  const canDeleteMessage = (message) => {
    if (!currentUser) return false;
    const adminRoles = [
      'founder',
      'co-founder',
      'manager',
      'administrator',
      'head-admin',
      'admin',
      'mod',
    ];
    return (
      adminRoles.includes(currentUser.role) || message.userId === currentUser.id
    );
  };

  const handleDeleteMessage = (messageId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    if (!ws || ws.readyState !== 1)
      return alert('Not connected to chat server!');
    ws.send(
      JSON.stringify({
        type: 'delete',
        messageId,
        userId: currentUser.id,
        // Optionally: user's role
        role: currentUser.role,
      })
    );
  };

  // DM UI open/close
  const handleOpenDm = (userId, username) => {
    setDmTarget(userId);
    setDmDmText('');
    setShowDmPanel(true);

    // Optional: filter past DMs (since they're appended above)
    setDmMessages((prevDms) => {
      const filtered = prevDms.filter(
        (dm) => dm.to === userId || dm.from === userId
      );
      return filtered;
    });
  };

  if (!currentUser) {
    return (
      <div className="chat-container">
        <div className="chat-header">
          <h2>💬 Chat</h2>
        </div>
        <div className="chat-login-required">
          <div className="login-prompt">
            <h3>Please Log In to Chat</h3>
            <p>You need to be logged in to participate in the chat.</p>
            <button
              className="login-btn"
              onClick={() => window.location.reload()}
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return showDmPanel ? (
    <div className="chat-container">
      <div className="chat-header">
        <button
          onClick={() => setShowDmPanel(false)}
          className="back-to-chat-btn"
        >
          ← Back to Chat
        </button>
        <h2>
          Private DM with{' '}
          {(() => {
            const found =
              usersOnline.find((u) => u.id === dmTarget) || { username: 'User' };
            return found.username;
          })()}
        </h2>
      </div>
      <div className="chat-messages" ref={chatContainerRef} style={{ height: 320, overflowY: 'auto' }}>
        {dmMessages
          .filter((dm) => (dm.from === currentUser.id && dm.to === dmTarget) || (dm.from === dmTarget && dm.to === currentUser.id))
          .map((dm, idx) => (
            <div
              key={dm.timestamp + idx}
              className={
                (dm.from === currentUser.id
                  ? 'dm-message-right'
                  : 'dm-message-left') + ' dm-message'
              }
            >
              <div
                className="message-username"
                style={{
                  color:
                    getRoleColor(
                      dm.from === currentUser.id
                        ? currentUser.role
                        : (usersOnline.find((u) => u.id === dmTarget) || {})
                            .role || 'user'
                    ),
                }}
              >
                {dm.from === currentUser.id
                  ? currentUser.discordUsername
                  : (usersOnline.find((u) => u.id === dmTarget) || {}).username ||
                    'User'}
              </div>
              <div className="message-content">{dm.message}</div>
              <div className="message-time">
                {new Date(dm.timestamp).toLocaleTimeString()}
              </div>
            </div>
          ))}
        <div ref={messagesEndRef} />
      </div>
      <form
        className="chat-input-form"
        onSubmit={handleSendDm}
        style={{ marginTop: 12 }}
      >
        <input
          type="text"
          className="chat-input"
          placeholder="Type your message..."
          value={dmDmText}
          autoFocus
          maxLength={500}
          onChange={(e) => setDmDmText(e.target.value)}
        />
        <button type="submit" className="chat-send-btn" disabled={!dmDmText.trim()}>
          Send
        </button>
      </form>
    </div>
  ) : (
    <div className="chat-container">
      <div className="chat-header">
        <h2>💬 Chat</h2>
        <span
          className={`chat-status ${isConnected ? 'online' : 'offline'}`}
          style={{
            color: isConnected ? 'green' : 'gray',
            marginLeft: 12,
            fontSize: 13,
          }}
        >
          {isConnected ? 'Connected' : 'Offline'}
        </span>
        <div className="chat-info">
          <span className="online-count">
            {usersOnline.length} online&nbsp;|&nbsp;
            {messages.length} messages
          </span>
        </div>
        <div className="user-list-dropdown">
          <span style={{ fontWeight: 700, fontSize: 13 }}>Online Users:</span>
          <div className="online-users-list">
            {[...usersOnline]
              .filter((u) => u.id !== currentUser.id)
              .map((user) => (
                <div
                  key={user.id}
                  className="online-user"
                  title="Start DM"
                  style={{
                    cursor: 'pointer',
                    color: getRoleColor(user.role),
                  }}
                  onClick={() => handleOpenDm(user.id, user.username)}
                >
                  {user.username}
                  <span
                    style={{
                      fontWeight: 400,
                      fontSize: 10,
                      marginLeft: 6,
                      color: '#ccc',
                    }}
                  >
                    {user.role}
                  </span>
                </div>
              ))}
            {usersOnline.filter((u) => u.id !== currentUser.id).length === 0 && (
              <div className="online-user" style={{ color: '#aaa' }}>
                No one else is online
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="chat-messages" ref={chatContainerRef}>
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`chat-message ${
              msg.role === 'system' ? 'system-message' : ''
            }`}
          >
            <div className="message-header">
              <span
                className="message-username"
                style={{
                  color: getRoleColor(msg.role),
                  cursor: onViewProfile ? 'pointer' : 'default',
                }}
                onClick={() => {
                  if (onViewProfile && msg.userId && msg.user !== 'System') {
                    onViewProfile(msg.userId);
                  }
                }}
              >
                {msg.user}
              </span>
              {msg.role !== 'system' && msg.role !== 'user' && (
                <span className="message-role">{msg.role}</span>
              )}
              <span className="message-time">
                {(() => {
                  const date = new Date(msg.timestamp);
                  const now = new Date();
                  const diff = now - date;
                  const minutes = Math.floor(diff / 60000);
                  if (minutes < 1) return 'just now';
                  if (minutes < 60) return `${minutes}m ago`;
                  if (date.toDateString() === now.toDateString()) {
                    return date.toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    });
                  }
                  return date.toLocaleDateString([], {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  });
                })()}
              </span>
              {canDeleteMessage(msg) && (
                <button
                  className="delete-message-btn"
                  onClick={() => handleDeleteMessage(msg.id)}
                  title="Delete message"
                >
                  ×
                </button>
              )}
            </div>
            <div className="message-content">
              {msg.message && <p>{msg.message}</p>}
              {msg.image && (
                <div className="message-image">
                  <img
                    src={msg.image}
                    alt="Chat attachment"
                    loading="lazy"
                  />
                </div>
              )}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>
      <form className="chat-input-form" onSubmit={handleSendMessage}>
        <div className="chat-input-column">
          <input
            type="text"
            className="chat-input"
            placeholder={`Chat as ${currentUser.discordUsername}...`}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            maxLength={500}
            disabled={
              isLoading ||
              permissions.canSendWorldMessages === false ||
              !isConnected
            }
          />
          <div className="chat-attachment-row">
            <button
              type="button"
              className="chat-attach-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={!permissions.canSendWorldImages || !isConnected}
              title={
                permissions.canSendWorldImages
                  ? 'Attach an image'
                  : 'Only founder-approved ranks can send images'
              }
            >
              📎 Attach Image
            </button>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              accept="image/*"
              onChange={handleAttachmentChange}
            />
            {imageAttachment && (
              <div className="chat-attachment-preview">
                <img
                  src={imageAttachment.dataUrl}
                  alt={imageAttachment.name}
                />
                <div>
                  <span>{imageAttachment.name}</span>
                  <button type="button" onClick={removeAttachment}>
                    Remove
                  </button>
                </div>
              </div>
            )}
          </div>
          {attachmentError && (
            <p className="chat-attachment-error">{attachmentError}</p>
          )}
        </div>
        <button
          type="submit"
          className="chat-send-btn"
          disabled={
            (!newMessage.trim() && !imageAttachment) ||
            isLoading ||
            permissions.canSendWorldMessages === false ||
            !isConnected
          }
        >
          Send
        </button>
      </form>
    </div>
  );
}

export default Chat;
